<script lang="ts">
  import { onMount, tick } from 'svelte';
  import { installClickSounds } from '../audio/sound';
  import { yieldToMain } from '../scene/Scene';
  import { get } from 'svelte/store';
  import type { AppContext } from '../app';
  import { hudShouldHide } from '../core/autoHide';
  import { addUtcMonths, formatUtc } from '../core/time';
  import { lang, t } from '../i18n';
  import { autoHideHud } from '../state/settings';
  import {
    SPEEDS,
    creditsOpen,
    currentDate,
    dockExpanded,
    focus,
    follow,
    hudHidden,
    introActive,
    panelExpanded,
    playback,
    speedIndex,
    titleCardHeight,
    photoMode,
    followTarget,
    panelInsets,
    selectedMission,
    selectMission,
    stepDate,
    stepSpeed,
    stopFollowing,
    togglePlay,
    toggleReverse,
    toggleScaleMode,
  } from '../state/stores';
  import Breadcrumb from './Breadcrumb.svelte';
  import FollowButton from './FollowButton.svelte';
  import FpsMeter from './FpsMeter.svelte';
  import LangToggle from './LangToggle.svelte';
  import ModeToggle from './ModeToggle.svelte';
  import Telemetry from './Telemetry.svelte';
  import Settings from './Settings.svelte';
  import PhotoMode from './PhotoMode.svelte';
  import TimeControls from './TimeControls.svelte';
  import TitleCard from './TitleCard.svelte';
  import Toasts from './Toasts.svelte';
  import DetailPanel from './DetailPanel.svelte';
  import MissionList from './MissionList.svelte';

  let list = $state<MissionList>();

  /**
   * The HUD mounts in stages, one task each (S4-03 part 6): header, telemetry, time controls, title card and toasts.
   * Mounted at once, its first layout (forced by the size bindings) made one 190 ms task at 4× CPU; Lighthouse counts
   * every task above 50.
   */
  let stage = $state(0);
  onMount(() => {
    void (async () => {
      for (let s = 1; s <= 4; s++) {
        await yieldToMain();
        stage = s;
      }
      performance.mark('zeitflug:hud-staged');
      // the final insets before the scene starts (its first framing fits them); the resize observer would report
      // them only after the next layout, with the scene already running
      await tick();
      reportInsets();
      onstaged?.();
    })();
  });

  let { ctx, onstaged }: { ctx: AppContext; onstaged?: () => void } = $props();

  // The scene frames into the area the HUD leaves free (S2-06): report the header and dock heights.
  let topEl: HTMLElement;
  let bottomEl: HTMLElement;
  function reportInsets(): void {
    // layout sizes, not bounding boxes: the intro slides the HUD off-screen with transforms (S2-12)
    const top = topEl.offsetHeight + get(titleCardHeight);
    const dock = bottomEl.offsetHeight;
    const panel = get(panelInsets);
    // the sheet sits above the dock, the side panel to the right of everything (S3-16)
    document.documentElement.style.setProperty('--dock-h', `${dock}px`);
    document.documentElement.style.setProperty('--panel-right', `${panel.right}px`);
    ctx.scene.setViewInsets(top, dock + panel.bottom, panel.right);
  }
  onMount(() => installClickSounds());
  onMount(() => titleCardHeight.subscribe(() => topEl && reportInsets()));
  onMount(() => panelInsets.subscribe(() => topEl && reportInsets()));

  const selected = $derived(ctx.missions.find((m) => m.id === $selectedMission) ?? null);

  onMount(() => {
    const ro = new ResizeObserver(reportInsets);
    ro.observe(topEl);
    ro.observe(bottomEl);
    window.addEventListener('resize', reportInsets);
    reportInsets();
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', reportInsets);
    };
  });

  // ── auto-hide (S4-15): while time plays, the HUD steps aside after a few seconds without input ──────────────

  let lastInput = 0;
  /** The click that ends a tap which only brought the hidden HUD back (event time, ms), so it does not pick. */
  let swallowClickBefore = 0;

  function input(): void {
    lastInput = performance.now();
    if (get(hudHidden)) hudHidden.set(false);
  }

  /** Keyboard focus inside the HUD, an expanded sheet or dock, or an open dialog keep the HUD on screen. */
  function held(): boolean {
    if (get(introActive) || get(photoMode) || get(panelExpanded) || get(dockExpanded)) return true;
    if (document.querySelector('dialog[open]')) return true;
    const el = document.activeElement;
    return !!el?.closest('.top, .bottom, [data-testid="detail-panel"]') && el.matches(':focus-visible');
  }

  onMount(() => {
    lastInput = performance.now();
    // capture phase: the HUD hears every input before the scene or a label does
    const onPointerDown = (e: PointerEvent): void => {
      const wasHidden = get(hudHidden);
      input();
      if (!wasHidden) return;
      // the first touch on a hidden HUD only brings it back (like a video player): no pick, no camera drag
      e.stopPropagation();
      swallowClickBefore = e.timeStamp + 1000;
    };
    const onClick = (e: MouseEvent): void => {
      if (e.timeStamp > swallowClickBefore) return;
      swallowClickBefore = 0;
      e.stopPropagation();
      e.preventDefault();
    };
    // Chrome sends a pointermove without movement when the layout under a resting cursor changes — as when the HUD
    // slides away; only a real move counts, or a cursor parked over the dock would bring the HUD back every time
    let pointerAt = { x: Number.NaN, y: Number.NaN };
    const onPointerMove = (e: PointerEvent): void => {
      if (e.clientX === pointerAt.x && e.clientY === pointerAt.y) return;
      pointerAt = { x: e.clientX, y: e.clientY };
      input();
    };
    const opts = { capture: true };
    window.addEventListener('pointerdown', onPointerDown, opts);
    window.addEventListener('click', onClick, opts);
    window.addEventListener('pointermove', onPointerMove, { capture: true, passive: true });
    for (const type of ['wheel', 'keydown', 'focusin'] as const) {
      window.addEventListener(type, input, { capture: true, passive: true });
    }
    const timer = setInterval(() => {
      const hide = hudShouldHide(
        { enabled: get(autoHideHud), playing: get(playback).playing, held: held(), lastInput },
        performance.now(),
      );
      if (hide !== get(hudHidden)) hudHidden.set(hide);
    }, 250);
    return () => {
      clearInterval(timer);
      window.removeEventListener('pointerdown', onPointerDown, opts);
      window.removeEventListener('click', onClick, opts);
      window.removeEventListener('pointermove', onPointerMove, opts);
      for (const type of ['wheel', 'keydown', 'focusin'] as const) {
        window.removeEventListener(type, input, opts);
      }
      hudHidden.set(false);
    };
  });

  /** While the HUD is hidden, a quiet caption keeps the date and speed in view (4 Hz is enough for it). */
  let captionDate = $state(0);
  $effect(() => {
    if (!$hudHidden) return;
    captionDate = get(currentDate);
    const id = setInterval(() => (captionDate = get(currentDate)), 250);
    return () => clearInterval(id);
  });
  const captionSpeed = $derived($t(`speed.${SPEEDS[speedIndex($playback.speed)]!.id}`));

  const DAY = 86_400_000;

  /** Keyboard shortcuts (S1-16). Native controls keep their own keys. */
  function onKeydown(e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey || e.altKey || get(introActive)) return;
    // in photo mode only P (back) and the photo toolbar's own Esc apply
    if (get(photoMode) && e.key !== 'p' && e.key !== 'P') return;
    const el = e.target as HTMLElement | null;
    // keys inside an open dialog (settings) belong to it — Esc must close the sheet, not stop following
    if (el?.closest('dialog')) return;
    const tag = el?.tagName;
    const inField = tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || el?.isContentEditable;
    const onButton = tag === 'BUTTON';
    switch (e.key) {
      case ' ':
        if (inField || onButton) return;
        togglePlay();
        break;
      case 'ArrowLeft':
      case 'ArrowRight': {
        if (inField) return;
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        if (e.shiftKey) stepDate(addUtcMonths(get(currentDate), dir) - get(currentDate));
        else stepDate(dir * DAY);
        break;
      }
      case 'ArrowUp':
      case 'ArrowDown':
        if (inField) return;
        stepSpeed(e.key === 'ArrowUp' ? 1 : -1);
        break;
      case 'r':
      case 'R':
        if (inField) return;
        toggleReverse();
        break;
      case 'p':
      case 'P':
        if (inField) return;
        photoMode.update((v) => !v);
        break;
      case 'l':
      case 'L':
        if (inField) return;
        list?.show();
        break;
      case 'm':
      case 'M':
        if (inField) return;
        toggleScaleMode();
        break;
      case 'f':
      case 'F': {
        if (inField) return;
        const id = get(focus);
        if (ctx.targets.get(id)?.kind === 'spacecraft') follow(id);
        break;
      }
      case 'Escape':
        // the open panel first (its own handler catches Esc only once its heading has the focus, which it gets a
        // moment after opening), then follow mode
        if (get(selectedMission)) selectMission(null);
        else if (get(followTarget)) stopFollowing();
        else return;
        break;
      default:
        return;
    }
    e.preventDefault();
  }
</script>

<svelte:window onkeydown={onKeydown} />

<!-- "About & credits" (S4-05): loaded when it is first opened, not with the app -->
{#if $creditsOpen}
  {#await import('./Credits.svelte') then { default: Credits }}
    <Credits />
  {/await}
{/if}

<header class="top" class:intro={$introActive || $photoMode} class:hidden={$hudHidden} bind:this={topEl}>
  <div class="stripe" aria-hidden="true"></div>
  {#if stage >= 1}
    <div class="bar">
      <Breadcrumb targets={ctx.targets} />
      <div class="top-right">
        <ModeToggle />
        <MissionList {ctx} bind:this={list} />
        <Settings />
        <LangToggle />
      </div>
    </div>
  {/if}
</header>

{#if stage >= 4}
  {#if !$photoMode}
    <Toasts {ctx} />
    <TitleCard {ctx} />
  {:else}
    <PhotoMode {ctx} />
  {/if}
{/if}

{#if selected && !$photoMode}
  {#key selected.id}
    <DetailPanel {ctx} mission={selected} />
  {/key}
{/if}

{#if ctx.deepLink.fps}
  <FpsMeter />
{/if}

<div class="bottom" class:intro={$introActive || $photoMode} class:hidden={$hudHidden} bind:this={bottomEl}>
  {#if stage >= 2}
    <!-- with the detail panel open, its own Follow button takes over -->
    {#if !selected}<FollowButton targets={ctx.targets} />{/if}
    <Telemetry {ctx} />
  {/if}
  {#if stage >= 3}
    <TimeControls {ctx} />
  {/if}
</div>

{#if $hudHidden}
  <!-- the date is still in the (hidden) dock for screen readers; this is only its quiet echo -->
  <div class="caption" aria-hidden="true" data-testid="hud-caption">
    {formatUtc(captionDate, $lang, 'day')} · {captionSpeed}
  </div>
{/if}

<style>
  /* Mission-console header band (style B): stripe, then a solid bar with 2 px rules between its parts. */
  .top {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    padding-top: env(safe-area-inset-top);
    background: var(--ink);
    border-bottom: var(--rule-w) solid var(--rule);
  }
  /* during the intro (S2-12) the HUD waits off-screen and slides in when it ends */
  .top,
  .bottom {
    transition:
      transform 420ms var(--ease),
      opacity 420ms var(--ease);
  }
  .top.intro,
  .top.hidden {
    transform: translateY(-110%);
  }
  .bottom.intro,
  .bottom.hidden {
    transform: translateY(110%);
  }
  /* auto-hidden (S4-15): out of the way and out of reach of taps, which go to the scene and bring it back */
  .top.hidden,
  .bottom.hidden,
  .bottom.hidden :global(> *) {
    opacity: 0;
    pointer-events: none;
  }
  @media (prefers-reduced-motion: reduce) {
    .top,
    .bottom {
      transition: none;
    }
    /* reduced motion: the HUD fades instead of sliding */
    .top.hidden,
    .bottom.hidden {
      transform: none;
      transition: opacity 300ms linear;
    }
  }
  :global(html[data-reduced-motion]) .top.hidden,
  :global(html[data-reduced-motion]) .bottom.hidden {
    transform: none;
    transition: opacity 300ms linear;
  }
  .caption {
    position: fixed;
    left: 50%;
    bottom: calc(env(safe-area-inset-bottom) + 10px);
    transform: translateX(-50%);
    padding: 3px 10px;
    background: color-mix(in srgb, var(--ink) 70%, transparent);
    color: var(--fg-muted);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    white-space: nowrap;
    pointer-events: none;
  }
  .stripe {
    height: 6px;
    background: var(--stripe);
  }
  .bar {
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    height: var(--bar-h);
    padding: 0 env(safe-area-inset-right) 0 env(safe-area-inset-left);
  }
  .top-right {
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-end;
  }
  .bottom {
    position: fixed;
    left: 0;
    right: var(--panel-right, 0px);
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 0 calc(env(safe-area-inset-right) + 8px) calc(env(safe-area-inset-bottom) + 8px)
      calc(env(safe-area-inset-left) + 8px);
    pointer-events: none;
  }
  .bottom :global(> *) {
    pointer-events: auto;
  }
  @media (max-width: 899px) {
    .bottom {
      gap: 2px;
      padding-bottom: calc(env(safe-area-inset-bottom) + 4px);
    }
  }
</style>
